import React from 'react';
import SectionInfoBox from '../../../components/SectionInfoBox';

interface ProjectGalleryProps {
    project: any;
    getImageUrl: (url: string) => string;
    openGallery: (images: string[], index: number) => void;
}

const ProjectGallery: React.FC<ProjectGalleryProps> = ({ project, getImageUrl, openGallery }) => {
    const firstActiveTab = () => {
        const defaultOrder = ['exterior', 'interior', 'amenities', 'construction'];
        for (const cat of defaultOrder) {
            if (project.gallery?.[cat]?.length > 0) return cat;
        }
        if (project.tpImage) return 'tp-map';
        
        // Find first non-empty custom category
        const otherKey = Object.keys(project.gallery || {}).find(k => 
            !defaultOrder.includes(k) && project.gallery[k]?.length > 0
        );
        return otherKey || '';
    };

    const [activeTab, setActiveTab] = React.useState(firstActiveTab());

    // Update active tab if it becomes empty or if initial firstActiveTab returns something new
    React.useEffect(() => {
        if (!activeTab) setActiveTab(firstActiveTab());
    }, [project.gallery, project.tpImage]);
    const scrollRef = React.useRef<HTMLDivElement>(null);

    const hasGalleryImages = project.gallery && (Object.values(project.gallery).some(arr => arr && (arr as any[]).length > 0));
    const hasTpImage = project.tpImage;
    
    if (!hasGalleryImages && !hasTpImage) return null;

    const renderGalleryGrid = (images: string[], type: string) => {
        const displayImages = images.slice(0, 3);
        const totalCount = images.length;

        return (
            <div className="flex flex-wrap -mx-3">
                {displayImages.map((img: string, i: number) => (
                    <div className="w-full md:w-1/3 px-3 mb-4" key={i}>
                        <div 
                            className="relative rounded-xl overflow-hidden shadow-sm hover:shadow-xl transition-all group cursor-pointer border border-slate-100"
                            onClick={() => openGallery(images, i)}
                        >
                            <img 
                                src={getImageUrl(img)}
                                className="w-full h-[200px] object-cover transition-transform duration-500 group-hover:scale-110"
                                alt={`${type} ${i + 1}`}
                            />
                            
                            {/* Overlay on the 3rd image if there are more */}
                            {i === 2 && totalCount > 3 && (
                                <div className="absolute inset-0 bg-black/70 backdrop-blur-[2px] flex flex-col items-center justify-center text-white border border-white/10 group-hover:bg-black/60 transition-all">
                                    <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center mb-2">
                                        <i className="fa-solid fa-images text-xl"></i>
                                    </div>
                                    <span className="text-xl font-bold">+{totalCount - 3} More</span>
                                    <span className="text-[12px] font-medium mt-1">View All Photos</span>
                                </div>
                            )}

                            {/* Hover info for 1st & 2nd images */}
                            {!(i === 2 && totalCount > 3) && (
                                <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                    <div className="w-10 h-10 rounded-full bg-white/30 backdrop-blur-md flex items-center justify-center text-white border border-white/20 scale-50 group-hover:scale-100 transition-transform">
                                        <i className="fa-solid fa-magnifying-glass-plus text-sm"></i>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    const scroll = (direction: 'left' | 'right') => {
        if (scrollRef.current) {
            const { scrollLeft, clientWidth } = scrollRef.current;
            const scrollAmount = clientWidth * 0.8;
            scrollRef.current.scrollTo({
                left: direction === 'left' ? scrollLeft - scrollAmount : scrollLeft + scrollAmount,
                behavior: 'smooth'
            });
        }
    };

    return (
        <div id="gallery" className="bg-white border border-slate-200 shadow-sm rounded-xl mb-4 overflow-hidden scroll-mt-32 transition-all hover:shadow-md relative">
            {/* Gallery Header - Title Only */}
            <div className="bg-white pt-6 pb-4 px-6 md:px-10 flex flex-col items-start lg:items-center text-center w-full">
                <div className="space-y-1">
                    <h4 className="text-xl md:text-3xl font-bold text-black leading-none flex items-center gap-2">
                        Project Documents & Gallery
                        <SectionInfoBox text={project.sectionInfo?.gallery} />
                    </h4>
                    <p className="text-black font-medium text-[12px] md:text-[14px]">
                        Discovery • Planning • Lifestyle Identity
                    </p>
                </div>
            </div>

            <div className="relative pt-6 px-4 md:px-10 pb-10 bg-white/50">
                {/* Floating Tabs Redesign with Arrows */}
                <div className="flex justify-center mb-8 sticky top-[12px] md:top-6 z-30 pointer-events-none">
                    <div className="w-full max-w-[95%] md:max-w-fit flex items-center bg-white/95 backdrop-blur-3xl p-1 md:p-1.5 rounded-2xl md:rounded-full border border-slate-200 shadow-[0_20px_50px_-15px_rgba(0,0,0,0.15)] pointer-events-auto relative group">
                        
                        {/* Left Arrow */}
                        <button 
                            onClick={() => scroll('left')}
                            className="flex shrink-0 w-8 h-8 md:w-10 md:h-10 items-center justify-center rounded-full bg-white/50 hover:bg-black hover:text-white transition-all active:scale-90 mr-1"
                        >
                            <i className="fa-solid fa-chevron-left text-[10px]"></i>
                        </button>

                        <div 
                            ref={scrollRef}
                            className="w-full overflow-x-auto no-scrollbar snap-x snap-mandatory scroll-smooth"
                        >
                            <ul className="flex items-center gap-1.5 flex-nowrap py-0.5" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                                {(() => {
                                    const tabs: { id: string, name: string, icon: string, imageIcon?: string | null }[] = [];
                                    const defaultOrder = ['exterior', 'interior', 'amenities', 'construction'];
                                    const galleryKeys = Object.keys(project.gallery || {});
                                    
                                    // Default tabs first
                                    defaultOrder.forEach(key => {
                                        if (project.gallery?.[key]?.length > 0) {
                                            const labels: Record<string, { name: string, icon: string }> = {
                                                exterior: { name: 'Exterior', icon: 'fa-camera-retro' },
                                                interior: { name: 'Interior', icon: 'fa-couch' },
                                                amenities: { name: 'Amenities', icon: 'fa-hotel' },
                                                construction: { name: 'Progress', icon: 'fa-trowel-bricks' }
                                            };
                                            tabs.push({ id: key, ...labels[key] });
                                        }
                                    });

                                    // TP Map tab
                                    if (project.tpImage) {
                                        tabs.push({ id: 'tp-map', name: 'TP Map', icon: 'fa-map-location-dot' });
                                    }

                                    // Custom tabs
                                    galleryKeys.forEach(key => {
                                        if (!defaultOrder.includes(key) && project.gallery[key]?.length > 0) {
                                            const customIcon = project.galleryIcons?.[key];
                                            tabs.push({ 
                                                id: key, 
                                                name: key.charAt(0).toUpperCase() + key.slice(1), 
                                                icon: customIcon && !customIcon.startsWith('http') ? customIcon : 'fa-images',
                                                imageIcon: customIcon && customIcon.startsWith('http') ? getImageUrl(customIcon) : null
                                            });
                                        }
                                    });

                                    return tabs.map(tab => (
                                        <li key={tab.id} className="shrink-0 snap-center">
                                            <button 
                                                onClick={() => setActiveTab(tab.id)}
                                                className={`flex items-center gap-2.5 px-6 py-2.5 md:px-8 font-bold text-[13px] md:text-[14px] rounded-xl md:rounded-full transition-all whitespace-nowrap ${activeTab === tab.id ? 'bg-gradient-to-r from-[#05144b] to-[#4c1d95] text-[#f1ae24] border border-[#f1ae24] shadow-lg scale-105 ring-1 ring-[#f1ae24]/20' : 'text-black hover:bg-slate-50'}`} 
                                            >
                                                {tab.imageIcon ? (
                                                    <img src={tab.imageIcon} className="w-4 h-4 rounded-full object-cover" alt="" />
                                                ) : (
                                                    <i className={`fa-solid ${tab.icon} text-[13px]`}></i>
                                                )}
                                                {tab.name}
                                            </button>
                                        </li>
                                    ));
                                })()}
                            </ul>
                        </div>

                        {/* Right Arrow */}
                        <button 
                            onClick={() => scroll('right')}
                            className="flex shrink-0 w-8 h-8 md:w-10 md:h-10 items-center justify-center rounded-full bg-white/50 hover:bg-black hover:text-white transition-all active:scale-90 ml-1"
                        >
                            <i className="fa-solid fa-chevron-right text-[10px]"></i>
                        </button>
                    </div>
                </div>

                <div className="relative z-10">
                    <div className="transition-all duration-300">
                        {activeTab === 'tp-map' ? (
                            project.tpImage && renderGalleryGrid([project.tpImage], 'Town Planning Map')
                        ) : (
                            project.gallery?.[activeTab] && renderGalleryGrid(project.gallery[activeTab], activeTab)
                        )}
                    </div>
                </div>
            </div>
            
            <style>{`
                .no-scrollbar::-webkit-scrollbar {
                    display: none;
                }
                .no-scrollbar {
                    -ms-overflow-style: none;
                    scrollbar-width: none;
                }
            `}</style>
        </div>
    );
};

export default ProjectGallery;
